import { Injectable } from '@angular/core';

/**
 * @service PreviousRouteService
 * @description Service for tracking the user's navigation history within the application.
 * This service allows the application to store and retrieve the URL of the previously visited route.
 *
 * It is commonly used for:
 * 1. **Navigation Control**: Implementing custom "Back" buttons that return to the logical previous page rather than browser history.
 * 2. **Post-Action Redirection**: Returning a user to their previous context after completing an action (e.g., logging in, filling a form).
 *
 * The service uses the browser's `localStorage` to persist this information, ensuring it survives page reloads.
 *
 * **Key Responsibilities:**
 * 1. **State Persistence**: Saving the previous route URL.
 * 2. **State Retrieval**: Accessing the saved route URL.
 *
 * @feature 1. Store previous route.
 * @feature 2. Retrieve previous route.
 *
 * @service-provider root (singleton, application-wide)
 *
 * @local-storage-keys
 * - undefined: The `STORAGE_KEY` property is currently uninitialized, so the key used in localStorage defaults to the string "undefined".
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class PreviousRouteService {
  /**
   * @private
   * @readonly
   * @type {string}
   * @description The key used to store the route in localStorage.
   * Note: Currently uninitialized in the class definition.
   */
  private readonly STORAGE_KEY: string;

  /**
   * @method setPreviousRoute
   * @description Stores the provided route string in localStorage.
   *
   * It performs a check to see if the route is different from the currently stored one
   * to avoid unnecessary writes to localStorage.
   *
   * @param {string} route - The URL string of the route to store.
   * @returns {void}
   * @side-effect Updates the value in `localStorage`.
   *
   * @example
   * // In a Route Guard or Component
   * this.previousRouteService.setPreviousRoute('/dashboard');
   */
  setPreviousRoute(route: string) {
    const storedRoute = localStorage.getItem(this.STORAGE_KEY);
    if (!storedRoute || storedRoute !== route) {
      localStorage.setItem(this.STORAGE_KEY, route);
    }
  }

  /**
   * @method getPreviousRoute
   * @description Retrieves the stored previous route from localStorage.
   *
   * @returns {string} The stored route string, or null if not found.
   *
   * @example
   * const prev = this.previousRouteService.getPreviousRoute();
   * if (prev) {
   *   this.router.navigateByUrl(prev);
   * }
   */
  getPreviousRoute(): string {
    return localStorage.getItem(this.STORAGE_KEY);
  }
}
